/**
 * SelectableElement - Wrapper for editable elements in Page Builder preview
 * 
 * Features:
 * - data-tc-element-id for identification
 * - Hover outline indicator
 * - Click to select element
 * - Selected state with orange highlight and label tooltip
 * - Prevents click-through issues
 */
import React, { useState, useCallback } from 'react';

const SelectableElement = ({
  elementId,
  elementLabel,
  elementType,
  isSelected,
  onSelect,
  children,
  className = '',
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback((e) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(elementId);
  }, [disabled, elementId, onSelect]);

  const handleMouseEnter = useCallback(() => {
    if (!disabled) setIsHovered(true);
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  if (disabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      data-tc-element-id={elementId}
      data-tc-element-type={elementType}
      data-tc-element-label={elementLabel}
      data-testid={`element-${elementId.replace(/\./g, '-')}`}
      className={`
        relative cursor-pointer transition-all duration-150
        ${className}
      `}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hover/Selected overlay border */}
      <div 
        className={`
          absolute inset-0 pointer-events-none z-20 rounded transition-all duration-150
          ${isSelected 
            ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-black/50 bg-orange-500/5' 
            : isHovered 
              ? 'ring-2 ring-blue-400/60 ring-offset-1 ring-offset-transparent bg-blue-400/5' 
              : ''
          }
        `}
        style={{ 
          margin: '-4px',
          padding: '4px',
        }}
      />
      
      {/* Element label tooltip - visible on hover or when selected */}
      {(isHovered || isSelected) && (
        <div 
          className={`
            absolute -top-7 left-1/2 -translate-x-1/2 z-30
            px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap
            pointer-events-none shadow-lg
            ${isSelected 
              ? 'bg-orange-500 text-white' 
              : 'bg-zinc-800 text-zinc-200 border border-zinc-700'
            }
          `}
        >
          {elementLabel}
          {isSelected && (
            <span className="ml-1 opacity-75">• Click to edit</span>
          )}
        </div>
      )}
      
      {/* Corner drag handle indicator when selected */}
      {isSelected && (
        <>
          <div className="absolute -top-1 -left-1 w-2 h-2 bg-orange-500 rounded-full z-30 pointer-events-none" />
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full z-30 pointer-events-none" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-orange-500 rounded-full z-30 pointer-events-none" />
          <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-orange-500 rounded-full z-30 pointer-events-none" />
        </>
      )}
      
      {/* Actual element content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default SelectableElement;
